/**
 * auth.js — Vuex module: Authentication
 *
 * Fungsi:
 * - menyimpan state user yang sedang login (user object + JWT token)
 * - handle action `login`, `logout`, `validate`
 * - translate pesan error backend yang generik jadi pesan Indonesia
 *   yang ramah user (lihat helper `friendlyLoginError`)
 * - inisialisasi state dari localStorage saat app pertama kali load,
 *   jadi user yang refresh halaman tidak perlu login ulang
 *
 * API (lewat authService):
 * - POST /auth/login    (action `login`)
 * - POST /auth/logout   (action `logout`)
 *
 * Side effect setelah login berhasil:
 * - dispatch `menu/fetchMenus` (fire-and-forget) supaya sidebar siap
 * - router.push('/') ke dashboard
 *
 * Side effect setelah logout:
 * - dispatch `menu/clear` supaya menu user sebelumnya tidak ke-cache
 * - router.push('/login')
 */
import { authService } from '../../helpers/authservice/auth.service';
import router from '../../router/index';

// Initialize state from localStorage
const storedUser = authService.getCurrentUser();
const storedToken = authService.getToken();

export const state = storedUser && storedToken
  ? { status: { loggedIn: true }, user: storedUser, token: storedToken }
  : { status: {}, user: null, token: null };

export const getters = {
  loggedIn(state) {
    return !!state.token && !!state.user;
  },
  currentUser(state) {
    return state.user;
  },
  token(state) {
    return state.token;
  },
};

/**
 * Ubah pesan error mentah dari API jadi pesan yang ramah ke user.
 *
 * Beberapa backend (termasuk Laravel default) mengembalikan pesan generik
 * seperti "Unauthorized", "Unauthenticated", atau "Invalid credentials"
 * untuk kasus password salah. Kita translate jadi pesan Indonesia yang
 * jelas konteksnya untuk halaman login.
 */
function friendlyLoginError(error) {
  // Tidak ada response (network error / timeout / CORS, dst)
  if (!error?.response) {
    return 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.';
  }

  const status = error.response.status;
  const data = error.response.data || {};
  const rawMessage = String(
    data.message || data.error || data.data || ''
  ).trim();
  const lower = rawMessage.toLowerCase();

  // Kredensial salah — biasanya 401 atau 422 dari /auth/login.
  // Pesan generik dari API ("Unauthorized", "Invalid credentials", dll)
  // kita ganti dengan pesan yang lebih jelas.
  const looksLikeBadCredentials =
    status === 401 ||
    status === 422 ||
    lower.includes('unauthorized') ||
    lower.includes('unauthenticated') ||
    lower.includes('invalid credential') ||
    lower.includes('credentials do not match') ||
    lower.includes('password') ||
    lower.includes('email');

  if (looksLikeBadCredentials) {
    // Tangani kasus account locked / disabled secara terpisah kalau ada
    if (lower.includes('locked') || lower.includes('disabled') || lower.includes('inactive')) {
      return 'Akun Anda tidak aktif. Silakan hubungi administrator.';
    }
    return 'Email atau password yang Anda masukkan salah.';
  }

  // Validation errors dari Laravel: { errors: { email: ["..."], password: ["..."] } }
  if (data.errors && typeof data.errors === 'object') {
    const firstKey = Object.keys(data.errors)[0];
    if (firstKey) {
      const val = data.errors[firstKey];
      const msg = Array.isArray(val) ? val[0] : val;
      if (msg) return String(msg);
    }
  }

  if (status >= 500) {
    return 'Server sedang bermasalah. Silakan coba lagi beberapa saat lagi.';
  }

  // Fallback: pakai pesan dari API kalau ada, kalau tidak ya generic.
  return rawMessage || 'Login gagal. Silakan coba lagi.';
}

export const actions = {
  /**
   * Login user via IHC Portal API.
   *
   * Fitur:
   * - call authService.login() (POST /auth/login)
   * - simpan token + user ke Vuex state + localStorage
   * - dispatch menu/fetchMenus (fire-and-forget) supaya sidebar siap
   * - redirect ke dashboard '/'
   * - translate error generik backend jadi pesan ramah user via friendlyLoginError()
   *
   * @throws Error dengan message yang sudah di-translate (untuk ditampilkan di login.vue)
   */
  async login({ commit, dispatch }, { email, password }) {
    commit('loginRequest', { email });
    try {
      const { token, user } = await authService.login(email, password);
      if (token) {
        commit('loginSuccess', { user, token });
        // Fetch menu (sidebar/navbar) untuk user ini — fire-and-forget,
        // jangan block redirect ke dashboard.
        dispatch('menu/fetchMenus', { force: true }, { root: true }).catch(() => {});
        router.push('/');
        return { user, token };
      }
      // Login berhasil dari sisi HTTP tapi backend tidak mengembalikan token —
      // anggap sebagai login gagal.
      const errorMsg = 'Email atau password yang Anda masukkan salah.';
      commit('loginFailure', errorMsg);
      dispatch('notification/error', errorMsg, { root: true });
      throw new Error(errorMsg);
    } catch (error) {
      const errorMsg = friendlyLoginError(error);
      commit('loginFailure', errorMsg);
      dispatch('notification/error', errorMsg, { root: true });
      // Lempar error baru dengan message yang sudah di-translate supaya
      // komponen login (login.vue) bisa pakai message ini langsung.
      const friendly = new Error(errorMsg);
      friendly.original = error;
      throw friendly;
    }
  },

  /**
   * Logout user via IHC Portal API.
   *
   * Fitur:
   * - call authService.logout() (POST /auth/logout)
   * - bersihkan state Vuex auth
   * - bersihkan menu state supaya user berikutnya dapat menu fresh
   * - redirect ke '/login'
   */
  async logout({ commit, dispatch }) {
    await authService.logout();
    commit('logout');
    // Bersihkan menu state juga supaya user berikutnya dapat menu fresh.
    dispatch('menu/clear', null, { root: true });
    router.push('/login');
  },

  /**
   * Validate current session — cek apakah token + user di state masih ada.
   *
   * Dipanggil saat app initialization untuk pastikan state Vuex (yang di-load
   * dari localStorage) konsisten. Kalau salah satu hilang, paksa logout.
   */
  validate({ commit, state }) {
    if (!state.token || !state.user) {
      commit('logout');
      return null;
    }
    return state.user;
  },
};

export const mutations = {
  loginRequest(state, { email }) {
    state.status = { loggingIn: true };
    state.user = { email };
  },
  loginSuccess(state, { user, token }) {
    state.status = { loggedIn: true };
    state.user = user;
    state.token = token;
  },
  loginFailure(state, error) {
    state.status = { error };
    state.user = null;
    state.token = null;
  },
  logout(state) {
    state.status = {};
    state.user = null;
    state.token = null;
  },
};